/**
 * Generic renderer for {@link SerializableDesign}. One function replaces every
 * per-design `build()` across all 11 widget catalogs: it substitutes
 * single-value slots, repeats list rows, evaluates %-driven geometry (via the
 * safe {@link evalFormula}) and splices it all into the design's `frame` SVG.
 *
 * SECURITY: this is also the single XML-escaping choke-point. Any slot marked
 * `escape` (default for `text`) is truncated then XML-escaped before it touches
 * the markup, so user-controlled values (chat text, social handles, playlist
 * titles, crawl text) can never inject SVG — for both first-party and
 * community-published designs.
 *
 * Además, `RenderData.untrusted` nombra los campos cuyo valor viene del
 * usuario (chat: name/text/time; crawl: USER_TEXT; playlist: títulos): esos
 * se escapan SIEMPRE, ignorando un `escape:false` en la metadata del diseño —
 * la metadata de un diseño de la comunidad no está validada por el servidor y
 * no puede decidir si el chat entra como markup. Todo color (valor vivo,
 * paleta, fallback) pasa por `safeColor` → `#rrggbb` o se descarta. El escape
 * también neutraliza `{` (→ `&#123;`) para que un texto con `{{W}}` no sea
 * reescrito por la pasada final de tokens escalares, y los splices usan un
 * replacer-función para que `$&`/`$'` en el contenido no expandan markup.
 */

import { evalFormula } from "./formulaEval";
import type {
  DesignSlot,
  DesignList,
  DesignFragment,
  SerializableDesign,
} from "./serializableDesign";

/** Live values bound to the design's slots/vars, plus optional list items. */
export interface RenderData {
  /** Named values for `slots` + numeric vars referenced by `computed`/`showIf`. */
  values?: Record<string, string | number | undefined>;
  /** Items for a list design (chat messages / playlist rows). */
  items?: Array<Record<string, string | number | undefined>>;
  /**
   * Nombres de slots / campos de fila cuyo valor viene del usuario. Renderizados
   * como text/time se XML-escapan SIEMPRE, aunque el diseño declare
   * `escape:false` (ver SECURITY arriba).
   */
  untrusted?: readonly string[];
}

// ── escaping / formatting primitives (shared choke-point) ───────────────────

const HEX = /^#[0-9a-fA-F]{6}$/;

const esc = (s: string): string =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    // `{{token}}` dentro de un texto escapado no debe sobrevivir a la pasada
    // final de `fill` (un mensaje "{{W}}" se reescribiría con el ancho).
    .replace(/\{/g, "&#123;");

/**
 * Único formato de color aceptado: literal `#rrggbb`. Se aplica al valor vivo
 * (authorColor), a la paleta del diseño y a los fallbacks — en un diseño de la
 * comunidad cualquiera de los tres podría traer `url(javascript:…)`.
 */
const safeColor = (v: unknown): string | undefined =>
  typeof v === "string" && HEX.test(v) ? v : undefined;

const trunc = (s: string, n: number): string => {
  const t = String(s ?? "");
  return t.length <= n ? t : `${t.slice(0, Math.max(0, n - 1)).trimEnd()}…`;
};

/** Spanish dot-grouped thousands ("1248" → "1.248"). */
const thousands = (n: number): string =>
  Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/**
 * Render a single slot value to its safe string form. `forceEscape` = el valor
 * viene del usuario (`RenderData.untrusted`): se escapa aunque la metadata del
 * diseño diga `escape:false`.
 */
function renderSlot(
  slot: DesignSlot,
  raw: string | number | undefined,
  forceEscape = false,
): string {
  if (slot.kind === "color") {
    return safeColor(raw) ?? safeColor(slot.fallback) ?? "#ffffff";
  }
  if (slot.kind === "number" || slot.kind === "pct") {
    const n = typeof raw === "number" ? raw : Number(raw);
    const v = Number.isFinite(n) ? n : 0;
    return slot.format === "thousands" ? thousands(v) : String(Math.round(v));
  }
  // text | time
  let s = raw == null ? (slot.fallback ?? "") : String(raw);
  if (typeof slot.cap === "number") s = trunc(s, slot.cap);
  const shouldEscape = forceEscape || (slot.escape ?? slot.kind === "text");
  return shouldEscape ? esc(s) : s;
}

/** Substitute `{{name}}` tokens from a resolved string map (single pass). */
function fill(template: string, map: Record<string, string>): string {
  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (whole, name) =>
    name in map ? map[name] : whole,
  );
}

// ── core ────────────────────────────────────────────────────────────────────

/** Build the numeric var map: numeric slot values + W/H + evaluated `computed`. */
function buildVars(
  design: SerializableDesign,
  values: Record<string, string | number | undefined>,
): Record<string, number> {
  // null-proto so a hostile computed name (`__proto__`, `constructor`) becomes a
  // plain own-key and can never mutate/leak the prototype chain.
  const vars: Record<string, number> = Object.create(null);
  vars.W = design.cardWidth;
  vars.H = design.cardHeight;
  for (const [k, v] of Object.entries(values)) {
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n)) vars[k] = n;
  }
  if (design.computed) {
    for (const [name, formula] of Object.entries(design.computed)) {
      const r = evalFormula(formula, vars);
      vars[name] = Number.isFinite(r) ? r : 0;
    }
  }
  return vars;
}

/** Resolve the `{{name}}` substitution map for frame/fragments (slots + vars). */
function resolveScalarMap(
  design: SerializableDesign,
  values: Record<string, string | number | undefined>,
  vars: Record<string, number>,
  untrusted: ReadonlySet<string>,
): Record<string, string> {
  const map: Record<string, string> = {};
  // computed + base numeric vars
  for (const [k, v] of Object.entries(vars)) map[k] = String(v);
  // slots (override vars with their formatted/escaped form)
  if (design.slots) {
    for (const [name, slot] of Object.entries(design.slots)) {
      map[name] = renderSlot(slot, values[name], untrusted.has(name));
    }
  }
  return map;
}

function renderList(
  list: DesignList,
  items: RenderData["items"],
  untrusted: ReadonlySet<string>,
): string {
  const recent = (items ?? []).slice(-list.maxRows);
  return recent
    .map((item, i) => {
      const rowMap: Record<string, string> = {
        ROW_INDEX: String(i),
        ROW_Y: String((list.baselineY ?? 0) + i * (list.rowHeight ?? 0)),
      };
      for (const [field, slot] of Object.entries(list.rowSlots)) {
        const raw = item[field];
        // a color slot whose value is absent/invalid falls back to the palette
        // (también saneada: la paleta/fallback de un diseño de la comunidad es
        // tan poco confiable como el valor vivo)
        if (slot.kind === "color") {
          const fromPalette = list.palette?.length
            ? list.palette[i % list.palette.length]
            : undefined;
          rowMap[field] =
            safeColor(raw) ??
            safeColor(fromPalette) ??
            safeColor(slot.fallback) ??
            "#ffffff";
          continue;
        }
        rowMap[field] = renderSlot(slot, raw, untrusted.has(field));
      }
      return fill(list.row, rowMap);
    })
    .join("");
}

/** Render one fragment (handling `showIf` + `repeat`) to its markup string. */
function renderFragment(
  frag: DesignFragment,
  scalarMap: Record<string, string>,
  vars: Record<string, number>,
): string {
  if (frag.repeat) {
    const idxVar = frag.repeat.var ?? "j";
    const count = Math.max(0, Math.floor(evalFormula(frag.repeat.count, vars)));
    let out = "";
    for (let j = 0; j < count; j++) {
      const localVars = { ...vars, [idxVar]: j };
      // expose the iteration index `j` to the fragment's placeholders/formulas;
      // base computed vars are referenced directly.
      const localMap = { ...scalarMap, [idxVar]: String(j) };
      if (frag.showIf && evalFormula(frag.showIf, localVars) === 0) continue;
      out += fill(frag.svg, localMap);
    }
    return out;
  }
  if (frag.showIf && evalFormula(frag.showIf, vars) === 0) return "";
  return fill(frag.svg, scalarMap);
}

/**
 * Render a serializable design to its final self-contained `<svg>` string.
 * `data.values` binds the live values (time, counts, handle, …); `data.items`
 * supplies list rows (chat/playlist). Crawl callers render the scroll segment
 * separately via {@link renderScrollSegment}.
 */
export function renderDesign(
  design: SerializableDesign,
  data: RenderData = {},
): string {
  const values = data.values ?? {};
  const untrusted = new Set(data.untrusted ?? []);
  const vars = buildVars(design, values);
  const scalarMap = resolveScalarMap(design, values, vars, untrusted);

  let svg = design.frame;

  // Replacer-función en los splices: con un string, `$&`/`$'`/`$1` dentro del
  // contenido (un mensaje de chat) se expandirían a markup del frame.
  if (design.list && svg.includes("{{ROWS}}")) {
    const rows = renderList(design.list, data.items, untrusted);
    svg = svg.replace("{{ROWS}}", () => rows);
  }
  if (design.fragments) {
    // group keyless → {{FRAGMENTS}}, keyed → {{FRAGMENTS:key}} (in catalog order)
    const buckets: Record<string, string> = {};
    for (const frag of design.fragments) {
      const marker = frag.key ? `{{FRAGMENTS:${frag.key}}}` : "{{FRAGMENTS}}";
      buckets[marker] =
        (buckets[marker] ?? "") + renderFragment(frag, scalarMap, vars);
    }
    for (const [marker, markup] of Object.entries(buckets)) {
      svg = svg.replace(marker, () => markup);
    }
  }
  return fill(svg, scalarMap);
}

/** Render the crawl scroll segment (chrome `frame` is rendered via renderDesign). */
export function renderScrollSegment(
  design: SerializableDesign,
  data: RenderData = {},
): string {
  if (!design.scroll) return "";
  const values = data.values ?? {};
  const untrusted = new Set(data.untrusted ?? []);
  const map: Record<string, string> = {};
  for (const [name, slot] of Object.entries(design.scroll.slots)) {
    map[name] = renderSlot(slot, values[name], untrusted.has(name));
  }
  return fill(design.scroll.segment, map);
}
